import { useEffect, useState } from 'react';

/**
 * Chart colours come from the theme's CSS custom properties, so every Recharts chart follows light
 * and dark mode. Spread the props from useChartTheme() into the chart parts instead of relying on
 * Recharts' defaults (a white tooltip and #666 ticks, which are unreadable in dark mode).
 */

/** Reads a theme colour, e.g. chartColor('--chart-emphasis'). */
export const chartColor = (name) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/** Charts read colours when they render, so redraw when the theme flips. */
export const useThemeRedraw = () => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const redraw = () => setTick((t) => t + 1);
    const observer = new MutationObserver(redraw);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', redraw);
    return () => {
      observer.disconnect();
      media.removeEventListener('change', redraw);
    };
  }, []);
};

/** The chart tooltip panel. */
export const tooltipBox = {
  background: 'var(--bg-overlay)',
  border: '1px solid var(--border-default)',
  borderRadius: 8,
  boxShadow: 'var(--shadow-medium)',
  padding: '8px 12px',
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
};

/**
 * Themed props for Recharts parts:
 *
 *   const theme = useChartTheme();
 *   <CartesianGrid {...theme.grid} />
 *   <XAxis dataKey="day" {...theme.xAxis} />
 *   <YAxis {...theme.yAxis} />
 *   <Tooltip {...theme.tooltip} />
 *   <Bar dataKey="n" fill={theme.colors.emphasis} />
 */
export const useChartTheme = () => {
  useThemeRedraw();
  const muted = chartColor('--fg-muted');
  const axisLine = chartColor('--border-default');
  const tick = { fill: muted, fontSize: 12 };
  return {
    grid: { stroke: chartColor('--border-subtle'), strokeDasharray: '3 3', vertical: false },
    xAxis: { stroke: axisLine, tick, tickLine: false },
    yAxis: { stroke: axisLine, tick, tickLine: false, axisLine: false },
    tooltip: {
      contentStyle: tooltipBox,
      labelStyle: { color: 'var(--fg-default)', fontWeight: 600, marginBottom: 4 },
      itemStyle: { color: 'var(--fg-default)' },
      cursor: { fill: chartColor('--bg-subtle'), stroke: chartColor('--border-default') },
    },
    legend: { wrapperStyle: { color: muted, fontSize: 12 } },
    colors: {
      emphasis: chartColor('--chart-emphasis'),
      context: chartColor('--chart-context'),
      neutral: chartColor('--chart-neutral'),
      ordinal: [chartColor('--chart-ordinal-1'), chartColor('--chart-ordinal-2'), chartColor('--chart-ordinal-3')],
      grid: chartColor('--border-subtle'),
      muted,
    },
  };
};
